import { useTranslation } from 'react-i18next';
import { useState } from 'react';

import {
  DecisionIQDoctorFrequency,
  DecisionIQMedicalSurvey,
  DecisionIQDentalSurvey,
  DecisionIQVisionSurvey,
} from 'enrollment/features/DecisionSupportServices/DecisionIQ';
import { DependentsResponseDto } from 'theSource/features/FamilyMembers/types';
import { SubscriberFieldsDto } from 'theSource/types';
import {
  DECISION_IQ_SURVEY_QUERY_KEY,
  DecisionIQSurveySubmissionDto,
} from 'enrollment/queries';
import { PsButtonTuner } from 'stencilSetup';
import { OrbitDrawer } from 'enrollment/components/Shared/OrbitDrawer';
import { queryClient } from 'theSource/utils';

interface DecisionIQSurveyDrawerProps {
  triggerLabel: string;
  subscriber?: SubscriberFieldsDto;
  dependents?: DependentsResponseDto;
  orgBenefitId?: number;
  benefitLookup?: string;
  onRecommendationSurveySubmit?: (arg0: DecisionIQSurveySubmissionDto) => void;
}

export const DecisionIQSurveyDrawer = ({
  triggerLabel,
  subscriber,
  dependents,
  orgBenefitId,
  benefitLookup,
  onRecommendationSurveySubmit,
}: DecisionIQSurveyDrawerProps) => {
  const { t } = useTranslation('pages.decisionIQ');
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isMedical = benefitLookup === 'medical';
  const isDental = benefitLookup === 'dental';
  const isVision = benefitLookup === 'vision';

  const eventSurveyDrawer = () => {
    setDrawerOpen(true);
  };

  const closeSurveyDrawer = () => {
    // refetching is a quick way to reset the contents of the drawer without overcomplicating the code
    queryClient.invalidateQueries(
      `${DECISION_IQ_SURVEY_QUERY_KEY}_${orgBenefitId}`
    );
    setDrawerOpen(false);
  };

  return (
    <>
      <PsButtonTuner
        buttonId="decisionIQSurveyDrawer-trigger"
        label={triggerLabel}
        level="secondary"
        extraClass="btn-secondary"
        onClick={eventSurveyDrawer}
      />
      <OrbitDrawer
        drawerId="decisionIQSurveyDrawer"
        heading={t('personalize_recs')}
        dialogLarge={true}
        isOpen={drawerOpen}
        footer={true}
        closeButtonLabel={t('close')}
        keepMounted={true}
        handleClose={closeSurveyDrawer}
      >
        <div>
          {isMedical && (
            <>
              <div className="mt-4">
                <DecisionIQDoctorFrequency
                  dependents={dependents}
                  subscriber={subscriber}
                  orgBenefitId={orgBenefitId || 0}
                  showTitle={false}
                  subtitleText={t('survey_drawer.take_a_moment')}
                />
              </div>
              <DecisionIQMedicalSurvey
                orgBenefitId={orgBenefitId || 0}
                dependents={dependents}
                subscriber={subscriber}
                isModalSurvey
                onSubmit={() => setDrawerOpen(false)}
                onRecommendationSurveySubmit={onRecommendationSurveySubmit}
                drawerOpen={drawerOpen}
              />
            </>
          )}
          {isDental && (
            <DecisionIQDentalSurvey
              orgBenefitId={orgBenefitId || 0}
              dependents={dependents}
              subscriber={subscriber}
              isModalSurvey
              onSubmit={() => setDrawerOpen(false)}
              onRecommendationSurveySubmit={onRecommendationSurveySubmit}
            />
          )}
          {isVision && (
            <DecisionIQVisionSurvey
              orgBenefitId={orgBenefitId || 0}
              dependents={dependents}
              subscriber={subscriber}
              isModalSurvey
              onSubmit={() => setDrawerOpen(false)}
              onRecommendationSurveySubmit={onRecommendationSurveySubmit}
            />
          )}
        </div>
      </OrbitDrawer>
    </>
  );
};
